// The create screen's state as a draft (pages/tickets/New.jsx): what a new, loaded, replayed
// or kept draft looks like on screen, and which choices are the agent's own. Pure, for
// node --test (test/web/draft-screen.test.js).
//
// The screen holds `{ caller, fields, composer, touched }` (draft-model.js). `touched` lists
// the DEFAULTABLE keys (form, queue, owner) the agent chose; the rest of those keys are only
// DISPLAYED with today's default and are left out of what is saved and filed, so they
// resolve when the draft is filed (docs/superpowers/specs/2026-10-13-multi-ticket-drafts-
// design.md, "Data model": absent versus null). Each child in `fields.children` has its own
// `touched` and follows the same rule (draft-model.js `childTouched`).

import { assigneeFor } from '../create/child-prefill.js';
import { DEFAULTABLE, childTouched, fromDraft } from './draft-model.js';

/** The same three slots as CallerPanel.jsx's EMPTY_CALLER (that file is JSX, so not importable here). */
export const EMPTY_CALLER = { organization: null, location: null, customer: null };

export const EMPTY_FIELDS = {
  channel: 'phone',
  priority: 'normal',
  // '' = not categorised; the macro applier's set_type writes here.
  type: '',
  status: 'new',
  status_id: null,
  queue_id: '',
  assigned_user_id: '',
  form_id: '',
  device_serial: '',
  tags: [],
  follower_user_ids: [],
  cc_customer_ids: [],
  custom_fields: {},
  // Related tickets to link once the ticket exists (docs/API.md §3.7) — a link needs
  // two ticket numbers and this one has none yet. `{ id, public_ref, subject }` rows.
  links: [],
  // The ticket this one is a sub-issue of (`?parent=` → `parent_ticket_id`), or null.
  // Kept in fields rather than beside them so it is saved with the draft.
  parent: null,
  // Other issues from the SAME call, each filed as a child of this ticket when it is
  // created — the (+) under the main ticket (child-tickets.js). Also saved with the draft.
  children: [],
};

export const EMPTY_COMPOSER = { subject: '', body_text: '', is_internal_note: true };

function hasValue(value) {
  if (value === null || value === undefined || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (value === false) return false;
  return true;
}

/**
 * Whether a draft is worth keeping: a form the agent only opened (default form, default
 * queue, the agent as owner, nothing typed) is not. `defaultAssigneeId` is the owner a
 * freshly opened form carries; only a CHANGED owner counts. (Moved here from the old
 * create/draft.js; legacy-slot.js keeps its own frozen copy.)
 */
export function draftHasContent({ caller, fields, composer } = {}, { defaultAssigneeId = '' } = {}) {
  if (caller?.customer || caller?.organization) return true;
  if (composer?.subject?.trim() || composer?.body_text?.trim()) return true;
  if (fields?.tags?.length || fields?.follower_user_ids?.length || fields?.cc_customer_ids?.length) return true;
  if (fields?.device_serial?.trim()) return true;
  if (fields?.assigned_user_id && String(fields.assigned_user_id) !== String(defaultAssigneeId)) return true;
  if (fields?.links?.length || fields?.parent || fields?.children?.length) return true;
  if (fields?.custom_fields && Object.values(fields.custom_fields).some(hasValue)) return true;
  return false;
}

/**
 * A new draft's screen: the empties, the agent shown as owner (docs/API.md §3.3, the
 * default the server applies to an absent owner), and nothing touched. Fresh objects.
 */
export function initialScreen(user) {
  return {
    caller: { ...EMPTY_CALLER },
    fields: {
      ...EMPTY_FIELDS,
      tags: [], follower_user_ids: [], cc_customer_ids: [], custom_fields: {}, links: [], children: [],
      assigned_user_id: assigneeFor(user),
    },
    composer: { ...EMPTY_COMPOSER },
    touched: [],
  };
}

/**
 * `loaded` (a `fromDraft` result, or anything of that shape) MERGED over `base`: a draft
 * holds only what was saved, with untouched keys absent, and the screen's controlled inputs
 * must never be handed `undefined`.
 */
export function mergeScreen(base, loaded = {}) {
  return {
    caller: { ...base.caller, ...(loaded.caller ?? {}) },
    fields: { ...base.fields, ...(loaded.fields ?? {}) },
    composer: { ...base.composer, ...(loaded.composer ?? {}) },
    touched: [...(loaded.touched ?? base.touched ?? [])],
  };
}

/**
 * A saved child as the screen shows it. Its untouched owner is the agent looking at it
 * (`assigneeFor`, the default the server gives an absent owner at filing); its untouched form
 * and queue are '' until `withChildConfigDefaults` shows today's defaults. None of these
 * enter `touched`. A child saved before children had a `touched` is kept as it was.
 */
function screenChild(child, user) {
  const touched = childTouched(child);
  const fields = { form_id: '', queue_id: '', assigned_user_id: '', ...(child?.fields ?? {}) };
  if (!touched.includes('assigned_user_id')) fields.assigned_user_id = assigneeFor(user);
  return { ...child, fields, touched };
}

/** The screen for a `GET /api/ticket-drafts/:id` row. */
export function screenFromDraft(draft, user) {
  const screen = mergeScreen(initialScreen(user), fromDraft(draft));
  if (Array.isArray(screen.fields.children)) {
    screen.fields = { ...screen.fields, children: screen.fields.children.map((child) => screenChild(child, user)) };
  }
  return screen;
}

/**
 * The screen for a buffered PUT body (draft-buffer.js) that the server has not taken. The
 * body carries only the caller's ids; a slot whose id the server's copy also has takes the
 * server's joined row (its label), any other is `{ id }` alone.
 */
export function screenFromBody(body, serverDraft, user) {
  const slot = (key) => {
    const id = body?.[`${key}_id`] ?? null;
    if (!id) return null;
    const row = serverDraft?.[key];
    return row && String(row.id) === String(id) ? row : { id };
  };
  return screenFromDraft({
    subject: body?.subject ?? null,
    payload: body?.payload,
    organization: slot('organization'),
    location: slot('location'),
    customer: slot('customer'),
  }, user);
}

/** `touched` with `key` added if it is a defaultable key the agent has just chosen. Same array when unchanged. */
export function addTouched(touched, key) {
  if (!DEFAULTABLE.includes(key) || touched.includes(key)) return touched;
  return [...touched, key];
}

/**
 * The form and queue the screen DISPLAYS for keys the agent has not chosen: today's
 * defaults from the config. A touched key is left alone, an explicit empty choice included.
 * Returns `fields` itself when nothing changes, so an effect calling it cannot loop.
 */
export function withConfigDefaults(fields, touched, config) {
  if (!config?.loaded) return fields;
  const chosen = new Set(touched ?? []);
  const defaults = {
    form_id: (config.forms ?? []).find((form) => form.is_default)?.id ?? '',
    queue_id: (config.queues ?? []).find((queue) => queue.is_default)?.id ?? '',
  };
  let next = fields;
  for (const [key, value] of Object.entries(defaults)) {
    if (chosen.has(key) || next[key] === value) continue;
    next = { ...next, [key]: value };
  }
  return next;
}

/**
 * `withConfigDefaults` for each child, against that child's own `touched`. Returns `fields`
 * itself when no child changes, so an effect calling it cannot loop.
 */
export function withChildConfigDefaults(fields, config) {
  if (!config?.loaded || !Array.isArray(fields?.children)) return fields;
  let changed = false;
  const children = fields.children.map((child) => {
    const next = withConfigDefaults(child.fields, childTouched(child), config);
    if (next === child.fields) return child;
    changed = true;
    return { ...child, fields: next };
  });
  return changed ? { ...fields, children } : fields;
}

/**
 * What a parent prefill (child-prefill.js) counts as chosen: the parent's form and queue,
 * copied deliberately. Never the owner: the prefill shows the agent, the same default as a
 * new ticket, so it stays absent and resolves to whoever files.
 */
export function prefillTouched(fields) {
  return ['form_id', 'queue_id'].filter((key) => Boolean(fields?.[key]));
}

/**
 * A customer record (`GET /api/customers/:id`) as the three caller slots, in the shapes
 * CallerPanel and child-prefill.js keep: the "New ticket" button on a customer's page.
 */
export function callerFromCustomer(customer) {
  const organization = customer.organization
    ? { id: customer.organization.id, name: customer.organization.name, domains: [], location_count: null }
    : null;
  const location = customer.location
    ? {
        id: customer.location.id,
        organization_id: customer.organization?.id ?? null,
        name: customer.location.name,
        account_number: customer.location.account_number ?? null,
        address: customer.location.address ?? null,
        phone: customer.location.phone ?? null,
        timezone: customer.location.timezone ?? null,
      }
    : null;
  return {
    organization,
    location,
    customer: {
      id: customer.id,
      name: customer.name,
      email: customer.email ?? null,
      phone: customer.phone ?? null,
      position: customer.position ?? null,
      organization_id: customer.organization?.id ?? null,
      location_id: customer.location?.id ?? null,
    },
  };
}

/**
 * Which caller slots of a reopened draft are THIN: the server joins only `{ id, name }`
 * (docs/API.md §12.1), so a location without `account_number` and a customer without
 * `email` lack what the panels and the macro placeholders read (account number, address,
 * phone, email). The organization is left as it is: the screen shows only its name.
 */
export function callerNeedsDetail(caller) {
  return {
    location: Boolean(caller?.location) && !('account_number' in caller.location),
    customer: Boolean(caller?.customer) && !('email' in caller.customer),
  };
}

/**
 * `caller` with its thin slots filled in: `location` from a store row (`GET /organizations/
 * :id/locations`), `customer` from a record (`GET /customers/:id`). A slot is replaced only
 * while it still holds the SAME id and is still thin, so a late answer never overwrites a
 * newer choice. Returns `caller` itself when nothing changes. Display only: what is saved
 * is the ids (draft-model.js `toSaveBody`), so this never makes a save.
 */
export function hydrateCaller(caller, { location, customer } = {}) {
  const thin = callerNeedsDetail(caller);
  let next = caller;
  if (location && thin.location && String(caller.location.id) === String(location.id)) {
    const { is_active: _active, ...row } = location;
    next = { ...next, location: { ...caller.location, ...row } };
  }
  if (customer && thin.customer && String(caller.customer.id) === String(customer.id)) {
    next = { ...next, customer: { ...caller.customer, ...callerFromCustomer(customer).customer } };
  }
  return next;
}

/**
 * A buffered edit that the server would not take on replay for a reason other than
 * staleness (a 5xx, a refused shape), measured against the copy the server then returned:
 *   none      no buffered edit.
 *   keep      built on the server's current revision (or, for a draft the server does not
 *             have, on revision 0): it fills the form and the controller saves it.
 *   conflict  built on an older revision: another PC saved since. Rebasing it would
 *             overwrite that save, so it is shown once and the server's copy is loaded.
 *   gone      built on a saved revision of a draft the server no longer has for this
 *             person: shown once, and the screen starts empty.
 */
export function bufferAgainstServer(entry, draft) {
  if (!entry) return 'none';
  if (draft) return draft.revision === entry.baseRevision ? 'keep' : 'conflict';
  return entry.baseRevision === 0 ? 'keep' : 'gone';
}

/**
 * Whether Keep can be offered for the old slot: it compares the slot's form and queue with
 * today's defaults, so it waits for those lists, and stays unavailable if either failed.
 */
export function legacyKeepReady(config) {
  if (!config?.loaded) return false;
  const failed = config.failed ?? [];
  return !failed.includes('forms') && !failed.includes('queues');
}

/**
 * The form and queue of `fields` the agent chose, by the old slot's rule: one is counted
 * as chosen only when it is set and differs from today's default (`formId`, `queueId`).
 */
function legacyChosen(fields, formId, queueId) {
  return [['form_id', formId], ['queue_id', queueId]]
    .filter(([key, current]) => fields?.[key] && String(fields[key]) !== String(current ?? ''))
    .map(([key]) => key);
}

/**
 * The old browser slot (legacy-slot.js) as a draft, on Keep. The slot could not tell a
 * chosen form, queue or owner from a defaulted one, so: the owner is NEVER touched (it is
 * dropped, and resolves to whoever files); the form and queue are touched only when they
 * differ from today's default (`formId`, `queueId`), and dropped otherwise. Each child
 * follows the same rule with a `touched` of its own: its owner is untouched and shown as
 * `assigneeId`, the CURRENT agent's default (child-prefill.js `assigneeFor`), never the old
 * screen's displayed owner, which may be the previous agent on a shared desk; its form and
 * queue are touched only when they differ from today's default (an untouched one is kept
 * on screen, where it is the default, and left out of what is saved).
 */
export function legacyKeep(legacy, { formId = '', queueId = '', assigneeId = '' } = {}) {
  const { assigned_user_id: _owner, ...fields } = legacy?.fields ?? {};
  if (Array.isArray(fields.children)) {
    fields.children = fields.children.map((child) => ({
      ...child,
      fields: { ...(child?.fields ?? {}), assigned_user_id: assigneeId },
      touched: legacyChosen(child?.fields, formId, queueId),
    }));
  }
  const touched = legacyChosen(fields, formId, queueId);
  for (const key of ['form_id', 'queue_id']) {
    if (!touched.includes(key)) delete fields[key];
  }
  return {
    caller: { ...EMPTY_CALLER, ...(legacy?.caller ?? {}) },
    fields,
    composer: { ...(legacy?.composer ?? {}) },
    touched,
  };
}

function canonical(value) {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === 'object') {
    const out = {};
    for (const key of Object.keys(value).sort()) out[key] = canonical(value[key]);
    return out;
  }
  return value;
}

/**
 * A PUT body's content as a string, whatever its key order and base revision: two saves
 * with the same key say the same thing.
 */
export function saveKey(body) {
  const { base_revision: _base, ...rest } = body ?? {};
  return JSON.stringify(canonical(rest));
}

/**
 * The text a refused save carried (a PUT body), so the agent can be shown it once: the
 * subject, the message, and each child's. '' when nothing was typed.
 */
export function unsavedText(body) {
  if (!body) return '';
  const parts = [];
  const subject = typeof body.subject === 'string' ? body.subject.trim() : '';
  if (subject) parts.push(`Subject: ${subject}`);
  const message = body.payload?.composer?.body_text?.trim();
  if (message) parts.push(message);
  (body.payload?.fields?.children ?? []).forEach((child, index) => {
    const childSubject = child?.composer?.subject?.trim() ?? '';
    const childMessage = child?.composer?.body_text?.trim() ?? '';
    if (!childSubject && !childMessage) return;
    parts.push([`Child ticket ${index + 1}: ${childSubject}`, childMessage].filter(Boolean).join('\n'));
  });
  return parts.join('\n\n');
}
